import { useState } from "react";
import { usePlayer } from "@/context/PlayerContext";
import { ArtworkPlaceholder } from "@/components/ArtworkPlaceholder";
import api from "@/lib/api";
import {
  ChevronDown, Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Repeat1,
  List as QueueIcon, Volume2, VolumeX, X, GripVertical, Trash2,
} from "lucide-react";

function fmt(s) {
  const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

/* =====================================================================
 * FULL-SCREEN PLAYER (Phase 6.8i)
 * Immersive LXN HQ Now Playing surface. Reuses the single <audio> element
 * from PlayerContext — never creates a parallel one — so navigating or
 * closing the player NEVER restarts playback.
 * ===================================================================== */
export function FullPlayer() {
  const p = usePlayer();
  const [queueOpen, setQueueOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);

  if (!p.fullPlayerOpen) return null;
  const cur = p.current;
  if (!cur) {
    // Nothing to play — just close
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A0A0C] text-zinc-400 text-sm" data-testid="full-player-empty">
        <button onClick={p.closeFullPlayer} className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/15">Close</button>
      </div>
    );
  }

  const openVersions = async () => {
    if (!cur.song?.id) return;
    try {
      const { data } = await api.get(`/songs/${cur.song.id}/versions`);
      setVersions(data || []);
    } catch (_) { setVersions([]); }
    setVersionsOpen(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col text-white" data-testid="full-player"
         style={{
           background: "linear-gradient(180deg, #1a0b2a 0%, #0A0A0C 55%, #000 100%)",
           paddingTop: "env(safe-area-inset-top, 0px)",
           paddingBottom: "env(safe-area-inset-bottom, 0px)",
         }}>
      {/* Header */}
      <header className="flex items-center justify-between px-5 pt-3 pb-2 shrink-0">
        <button onClick={p.closeFullPlayer} data-testid="full-player-minimize"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center">
          <ChevronDown className="w-5 h-5" />
        </button>
        <div className="text-center min-w-0 px-3">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400">Now Playing</div>
          <div className="text-xs text-zinc-300 truncate">{cur.song?.artist || "LXN"}</div>
        </div>
        <button onClick={() => setQueueOpen(true)} data-testid="full-player-queue-open"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center relative">
          <QueueIcon className="w-5 h-5" />
          {p.queue?.length > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-violet-500 text-[9px] mono flex items-center justify-center">{p.queue.length}</span>
          )}
        </button>
      </header>

      {/* Artwork */}
      <div className="flex-1 min-h-0 flex items-center justify-center px-6">
        <div className="w-full max-w-md aspect-square rounded-2xl overflow-hidden shadow-[0_20px_80px_rgba(0,0,0,0.6)]">
          <ArtworkPlaceholder title={cur.song?.name || ""} artworkId={cur.song?.artwork_id} size="full"
                              className="w-full h-full" />
        </div>
      </div>

      {/* Meta + scrubber + controls */}
      <div className="shrink-0 px-6 pb-4 space-y-3">
        <div className="text-center">
          <div className="text-xl font-semibold truncate" data-testid="full-player-title">{cur.song?.name || "—"}</div>
          <button onClick={openVersions} data-testid="full-player-version"
                  className="mt-1 inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase text-violet-300 hover:text-violet-200">
            {cur.version?.label || "V"} {cur.version?.is_final ? "· FINAL" : ""}
          </button>
        </div>
        <div>
          <input
            type="range" min={0} max={p.duration || 0} step="0.1" value={p.progress}
            onChange={(e) => p.seek(parseFloat(e.target.value))}
            className="w-full h-1 accent-violet-500"
            data-testid="full-player-scrubber"
          />
          <div className="flex justify-between text-[10px] mono text-zinc-500 tabular mt-1">
            <span>{fmt(p.progress)}</span>
            <span>-{fmt(Math.max(0, (p.duration || 0) - p.progress))}</span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <button onClick={p.toggleShuffle} data-testid="full-player-shuffle"
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${p.shuffle ? "text-violet-400" : "text-zinc-500 hover:text-zinc-300"}`}>
            <Shuffle className="w-4 h-4" />
          </button>
          <button onClick={p.prevTrack} data-testid="full-player-prev"
                  className="w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center">
            <SkipBack className="w-5 h-5" />
          </button>
          <button onClick={p.toggle} data-testid="full-player-play"
                  className="w-16 h-16 rounded-full bg-white text-black hover:bg-white/90 flex items-center justify-center shadow-[0_8px_28px_rgba(255,255,255,0.15)]">
            {p.playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-0.5" />}
          </button>
          <button onClick={p.nextTrack} data-testid="full-player-next"
                  className="w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center">
            <SkipForward className="w-5 h-5" />
          </button>
          <button onClick={p.cycleRepeat} data-testid="full-player-repeat"
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${p.repeat !== "off" ? "text-violet-400" : "text-zinc-500 hover:text-zinc-300"}`}>
            {p.repeat === "one" ? <Repeat1 className="w-4 h-4" /> : <Repeat className="w-4 h-4" />}
          </button>
        </div>

        <div className="flex items-center gap-2 pt-1">
          {p.volume === 0 ? <VolumeX className="w-4 h-4 text-zinc-500" /> : <Volume2 className="w-4 h-4 text-zinc-500" />}
          <input
            type="range" min={0} max={1} step="0.01" value={p.volume}
            onChange={(e) => p.setVolume(parseFloat(e.target.value))}
            className="flex-1 h-1 accent-violet-500"
            data-testid="full-player-volume"
          />
        </div>
      </div>

      {queueOpen && <QueueSheet onClose={() => setQueueOpen(false)} />}
      {versionsOpen && <VersionsSheet versions={versions} onClose={() => setVersionsOpen(false)} />}
    </div>
  );
}

function QueueSheet({ onClose }) {
  const p = usePlayer();
  const [dragFrom, setDragFrom] = useState(null);
  return (
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={onClose} data-testid="queue-sheet">
      <div className="w-full md:max-w-md bg-[#141416] border border-white/10 rounded-t-2xl md:rounded-2xl p-4 max-h-[80vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium">Queue</div>
          <div className="flex items-center gap-2">
            <button onClick={p.clearQueue} className="text-[10px] tracking-widest uppercase text-rose-300 hover:text-rose-200">Clear</button>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
        </div>
        {p.queue?.length ? (
          <ul className="space-y-1">
            {p.queue.map((s, i) => (
              <li key={`${s.id}-${i}`}
                  draggable
                  onDragStart={() => setDragFrom(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (dragFrom != null && dragFrom !== i) p.reorderQueue(dragFrom, i); setDragFrom(null); }}
                  data-testid={`queue-item-${i}`}
                  className={`flex items-center gap-2 p-2 rounded-lg ${i === p.queueIndex ? "bg-violet-500/15 border border-violet-500/30" : "bg-white/[0.02]"}`}>
                <GripVertical className="w-3.5 h-3.5 text-zinc-600 shrink-0 cursor-grab" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{s.name}</div>
                  <div className="text-[10px] text-zinc-500 mono">{i === p.queueIndex ? "Playing" : `#${i + 1}`}</div>
                </div>
                <button onClick={() => p.removeFromQueue(i)} data-testid={`queue-remove-${i}`}
                        className="w-7 h-7 rounded-full hover:bg-rose-500/15 text-zinc-500 hover:text-rose-300 flex items-center justify-center">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center text-[11px] text-zinc-500 py-8">Queue is empty.</div>
        )}
      </div>
    </div>
  );
}

function VersionsSheet({ versions, onClose }) {
  const p = usePlayer();
  return (
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center" onClick={onClose} data-testid="versions-sheet">
      <div className="w-full md:max-w-sm bg-[#141416] border border-white/10 rounded-t-2xl md:rounded-2xl p-4 max-h-[70vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium">Versions</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        {versions.length ? (
          <ul className="space-y-1">
            {versions.map((v) => (
              <li key={v.id}>
                <button onClick={() => { p.play(p.current.song, v); onClose(); }}
                        data-testid={`version-${v.id}`}
                        className={`w-full text-left flex items-center gap-2 p-2 rounded-lg ${p.current?.version?.id === v.id ? "bg-violet-500/15 border border-violet-500/30" : "bg-white/[0.02] hover:bg-white/5"}`}>
                  <span className="mono text-[10px] px-1.5 py-0.5 rounded bg-violet-600/20 text-violet-300">{v.label}</span>
                  {v.is_final && <span className="text-[9px] tracking-widest uppercase text-emerald-300">Final</span>}
                  <span className="text-[11px] text-zinc-500 ml-auto mono">{v.uploaded_at ? new Date(v.uploaded_at).toLocaleDateString() : ""}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center text-[11px] text-zinc-500 py-8">No versions.</div>
        )}
      </div>
    </div>
  );
}
